import {Divider, Typography} from '@material-ui/core';
import Drawer from '@material-ui/core/Drawer';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText';
import Home from '@material-ui/icons/Home';
import AgencyAdministrationRouteConstants from 'AgencyAdministration/AgencyAdministrationRouteConstants';
import Ten99RouteConstants from 'Agent/1099s/Ten99RouteConstants';
import AccountRouteConstants from 'Agent/Accounts/AccountRouteConstants';
import {
  default as AgentDocumentationRouteConstants,
  default as AgentDocumentationRoutes,
} from 'Agent/AgentDocumentation/AgentDocumentationRouteConstants';
import {AgentInformationRouteConstants} from 'Agent/AgentInformation/AgentInformationRouteConstants';
import CommissionRouteConstants from 'Agent/Commissions/CommissionsRouteConstants';
import {changeCorrespondenceFilterPopup} from 'Agent/Policies/ducks/policyCorrespondence';
import PolicyRouteConstants from 'Agent/Policies/PolicyRouteConstants';
import * as AgentNewsAdministrationRouteConstants from 'AgentNewsAdministration/AgentNewsAdministrationRouteConstants';
import FormMenu from 'Core/components/Navigation/FormMenu';
import 'Core/components/Navigation/MyAccessMenu.sass';
import 'Core/components/Navigation/Sidebar.sass';
import CoreRouteConstants from 'Core/CoreRouteConstants';
import {
  getAndSyncSchooxUser,
  isSchooxPopupClosedEvent,
  redirectSchoox,
  toggleSchooxConfirmation,
} from 'Core/ducks/schoox';
import {CustomerSupportRouteConstants} from 'CustomerSupport/Routes';
import * as O from 'fp-ts/lib/Option';
import React, {useEffect} from 'react';
import {connect, useDispatch, useSelector} from 'react-redux';
import {RouteComponentProps, useLocation, withRouter} from 'react-router-dom';
import {ReducerState} from 'rootReducer';
import 'Shared/components/Modal.sass';
import NestedList from 'Shared/components/NestedList';
import RequireAtLeastOneScope from 'Shared/components/RequireAtLeastOneScope';
import RequireScope from 'Shared/components/RequireScope';
import {Scope} from 'Shared/constants/Scope';
import {isUserEmulating} from 'Shared/utilities/impersonationHelpers';
import {UserRouteConstants} from 'Users/Routes';
import {selectUserScopes} from 'Users/selectors/userProfileSelectors';
import {Schoox} from '../Schoox';
import {DocumentRouteConstants} from 'Agent/DocumentUploadHistory/Routes';

export type SidebarProps = {
  isOpen: boolean;
  onSidebarClose: () => void;
} & ReturnType<typeof mapStateToProps> &
  RouteComponentProps;

export const Sidebar = (props: SidebarProps) => {
  const withSidebarClose = (func: () => void) => () => {
    func();
    props.onSidebarClose();
  };
  const {pathname} = useLocation();
  const isAtCurrentRoute = (value: string) => pathname === value;

  const isAtMyAccessRoute = [
    AccountRouteConstants.ACCOUNTS,
    CommissionRouteConstants.COMMISSIONS,
    AgentInformationRouteConstants.NY_RATE_GENERATORS,
    PolicyRouteConstants.POLICIES,
    PolicyRouteConstants.CORRESPONDENCE_SUMMARIES(),
    Ten99RouteConstants.TEN99s,
  ].some(isAtCurrentRoute);

  const {selectedAgency, userClaims} = useSelector((state: ReducerState) => state.user.profile);
  const {enable_portals_virus_scan} = useSelector((state: ReducerState) => state.featureFlag);
  const {BR_Footer} = useSelector((state: ReducerState) => state.featureFlag);
  const {isSchooxConfirmationOpen, isLoading, isComplete, schooxUser, isSchooxPopupClosed, isSynced} = useSelector(
    (state: ReducerState) => state.schoox
  );

  const dispatch = useDispatch();

  const isImpersonating = isUserEmulating(userClaims);

  useEffect(() => {
    if (!isSchooxConfirmationOpen && isComplete && schooxUser.isSynced && !isSchooxPopupClosed) {
      dispatch(toggleSchooxConfirmation(true));
    }
  }, [isComplete]);

  return (
    <>
      <Schoox />
      <nav>
        <Drawer open={props.isOpen} onClose={props.onSidebarClose}>
          <div className="sidebar">
            <Typography className="sidebar__header" variant="h6" color="primary">
              Main Menu
            </Typography>
            <Divider />
            <List>
              <ListItem
                selected={isAtCurrentRoute(CoreRouteConstants.DASHBOARD)}
                button
                onClick={withSidebarClose(() => props.history.push(CoreRouteConstants.DASHBOARD))}
              >
                <ListItemIcon>
                  <Home className="sidebar__dashboard-icon" />
                </ListItemIcon>
                <ListItemText primary={'Home'} />
              </ListItem>
              <RequireAtLeastOneScope
                scopes={[
                  Scope.SearchAccounts,
                  Scope.PolicySearch,
                  Scope.SearchTaxStatements,
                  Scope.SearchCommissions,
                  Scope.ViewPendingUnderwritingRequirements,
                ]}
              >
                <NestedList isExpanded={isAtMyAccessRoute} mainListItemTitle="My Access">
                  <>
                    <RequireScope scope={Scope.PolicySearch} scopes={props.scopes}>
                      <ListItem
                        selected={isAtCurrentRoute(PolicyRouteConstants.POLICIES)}
                        button
                        onClick={withSidebarClose(() => props.history.push(PolicyRouteConstants.POLICIES))}
                      >
                        <ListItemIcon>
                          <i className="fa fa-file-alt sidebar__menu-icon" />
                        </ListItemIcon>
                        <ListItemText primary={'Policies'} />
                      </ListItem>
                    </RequireScope>
                    <RequireScope scope={Scope.ViewCorrespondenceDetails} scopes={props.scopes}>
                      <ListItem
                        selected={isAtCurrentRoute(PolicyRouteConstants.CORRESPONDENCE_SUMMARIES())}
                        button
                        onClick={withSidebarClose(() => {
                          props.history.push(PolicyRouteConstants.CORRESPONDENCE_SUMMARIES());
                          dispatch(changeCorrespondenceFilterPopup(true));
                        })}
                      >
                        <ListItemIcon>
                          <i className="fa fa-envelope-open-text sidebar__menu-icon" />
                        </ListItemIcon>
                        <ListItemText primary={'Correspondence'} />
                      </ListItem>
                    </RequireScope>
                    {enable_portals_virus_scan && (
                    <ListItem
                      selected={isAtCurrentRoute(DocumentRouteConstants.ROOT_URI)}
                      button
                      onClick={withSidebarClose(() => props.history.push(DocumentRouteConstants.ROOT_URI))}
                    >
                      <ListItemIcon>
                        <i className="fa fa-file-alt sidebar__menu-icon" />
                      </ListItemIcon>
                      <ListItemText primary={'Document Upload History'} />
                    </ListItem>
                    )}
                    <RequireScope scope={Scope.SearchCommissions} scopes={props.scopes}>
                      <ListItem
                        selected={isAtCurrentRoute(CommissionRouteConstants.COMMISSIONS)}
                        button
                        onClick={withSidebarClose(() => props.history.push(CommissionRouteConstants.COMMISSIONS))}
                      >
                        <ListItemIcon>
                          <i className="fa fa-money-check-alt sidebar__menu-icon" />
                        </ListItemIcon>
                        <ListItemText primary={'Commissions'} />
                      </ListItem>
                    </RequireScope>

                    {O.isSome(selectedAgency) && selectedAgency.value.isNewYork && (
                      <ListItem
                        selected={isAtCurrentRoute(AgentInformationRouteConstants.NY_RATE_GENERATORS)}
                        button
                        onClick={withSidebarClose(() =>
                          props.history.push(AgentInformationRouteConstants.NY_RATE_GENERATORS)
                        )}
                      >
                        <ListItemIcon>
                          <i className="fa fa-money-check-alt sidebar__menu-icon" />
                        </ListItemIcon>
                        <ListItemText primary={'Rate Generators'} />
                      </ListItem>
                    )}

                    <RequireScope scope={Scope.SearchTaxStatements} scopes={props.scopes}>
                      <ListItem
                        selected={isAtCurrentRoute(Ten99RouteConstants.TEN99s)}
                        button
                        onClick={withSidebarClose(() => props.history.push(Ten99RouteConstants.TEN99s))}
                      >
                        <ListItemIcon>
                          <i className="fa fa-file-invoice-dollar sidebar__menu-icon" />
                        </ListItemIcon>
                        <ListItemText primary={'1099s'} />
                      </ListItem>
                    </RequireScope>
                    <RequireScope scope={Scope.SendServiceRequest} scopes={props.scopes}>
                      <ListItem
                        selected={isAtCurrentRoute(PolicyRouteConstants.POLICY_SERVICE_REQUEST)}
                        button
                        onClick={withSidebarClose(() =>
                          props.history.push(PolicyRouteConstants.POLICY_SERVICE_REQUEST)
                        )}
                      >
                        <ListItemIcon>
                          <i className="fa fa-handshake sidebar__menu-icon" />
                        </ListItemIcon>
                        <ListItemText primary={'Policy Service Request'} />
                      </ListItem>
                    </RequireScope>
                    <RequireScope scope={Scope.SearchAccounts} scopes={props.scopes}>
                      <ListItem
                        selected={isAtCurrentRoute(AccountRouteConstants.ACCOUNTS)}
                        button
                        onClick={withSidebarClose(() => props.history.push(AccountRouteConstants.ACCOUNTS))}
                      >
                        <ListItemIcon>
                          <i className="fa fa-briefcase sidebar__menu-icon" />
                        </ListItemIcon>
                        <ListItemText primary={'Accounts'} />
                      </ListItem>
                    </RequireScope>
                    <RequireScope scope={Scope.ViewAgentHierarchy} scopes={props.scopes}>
                      <ListItem
                        selected={isAtCurrentRoute(AgentInformationRouteConstants.HIERARCHY)}
                        button
                        onClick={withSidebarClose(() => props.history.push(AgentInformationRouteConstants.HIERARCHY))}
                      >
                        <ListItemIcon>
                          <i className="fa fa-users sidebar__menu-icon" />
                        </ListItemIcon>
                        <ListItemText primary={'Agent Hierarchy'} />
                      </ListItem>
                    </RequireScope>
                    <RequireScope scope={Scope.SearchCommissionSchedules} scopes={props.scopes}>
                      <ListItem
                        selected={isAtCurrentRoute(CommissionRouteConstants.COMMISSION_SCHEDULES)}
                        button
                        onClick={withSidebarClose(() =>
                          props.history.push(CommissionRouteConstants.COMMISSION_SCHEDULES)
                        )}
                      >
                        <ListItemIcon>
                          <i className="fa fa-calendar-alt sidebar__menu-icon" />
                        </ListItemIcon>
                        <ListItemText primary={'Commission Schedules'} />
                      </ListItem>
                    </RequireScope>
                    <RequireScope scope={Scope.SearchAccounts} scopes={props.scopes}>
                      <ListItem
                        button
                        onClick={withSidebarClose(() =>
                          props.history.push(AccountRouteConstants.POLICY_INFORMATION_REPORTS)
                        )}
                      >
                        <ListItemIcon>
                          <i className="fa fa-info sidebar__menu-icon" />
                        </ListItemIcon>
                        <ListItemText primary={'Policy Information Reports'} />
                      </ListItem>
                    </RequireScope>
                    <RequireScope scope={Scope.ViewPendingUnderwritingRequirements} scopes={props.scopes}>
                      <ListItem
                        selected={isAtCurrentRoute(PolicyRouteConstants.PENDING_UNDERWRITING_REQUIREMENT)}
                        button
                        onClick={withSidebarClose(() =>
                          props.history.push(PolicyRouteConstants.PENDING_UNDERWRITING_REQUIREMENT)
                        )}
                      >
                        <ListItemIcon>
                          <i className="fa fa-file sidebar__menu-icon" />
                        </ListItemIcon>
                        <ListItemText primary={'Pending Underwriting Requirements'} />
                      </ListItem>
                    </RequireScope>
                    {O.isSome(selectedAgency) && !selectedAgency.value.isNewYork && (
                      <RequireScope scope={Scope.AgentResources} scopes={props.scopes}>
                        <>
                          {!isImpersonating && (
                            <ListItem
                              button
                              onClick={withSidebarClose(() => {
                                if (!isLoading && isComplete && isSynced) {
                                  dispatch(redirectSchoox());
                                } else {
                                  dispatch(toggleSchooxConfirmation(true));
                                  dispatch(isSchooxPopupClosedEvent(false));
                                  dispatch(getAndSyncSchooxUser());
                                }
                              })}
                            >
                              <ListItemIcon>
                                <i className="fas fa-chalkboard-teacher sidebar__menu-icon" />
                              </ListItemIcon>
                              <ListItemText primary={'Training Resources'} />
                            </ListItem>
                          )}
                          <ListItem
                            button
                            onClick={withSidebarClose(() => window.open('http://allstatevoluntary.com/qrt/qrtrd.php'))}
                          >
                            <ListItemIcon>
                              <i className="fa fa-lightbulb sidebar__menu-icon" />
                            </ListItemIcon>
                            <ListItemText primary={"Quoting & Add'l Info"} />
                          </ListItem>
                        </>
                      </RequireScope>
                    )}
                    <RequireScope scope={Scope.AgentLicense} scopes={props.scopes}>
                      <ListItem
                        button
                        onClick={withSidebarClose(() =>
                          props.history.push(AgentInformationRouteConstants.AGENT_LICENSES)
                        )}
                      >
                        <ListItemIcon>
                          <i className="fa fa-id-badge sidebar__menu-icon" />
                        </ListItemIcon>
                        <ListItemText primary={'Agent Licenses'} />
                      </ListItem>
                    </RequireScope>
                    <RequireScope scope={Scope.SearchEnrollmentApplications} scopes={props.scopes}>
                      <ListItem
                        button
                        onClick={withSidebarClose(() => props.history.push(PolicyRouteConstants.POLICY_APPLICATIONS))}
                      >
                        <ListItemIcon>
                          <i className="fa fa-file-signature sidebar__menu-icon" />
                        </ListItemIcon>
                        <ListItemText primary={'Policy Applications'} />
                      </ListItem>
                    </RequireScope>
                  </>
                </NestedList>
              </RequireAtLeastOneScope>
              <RequireAtLeastOneScope
                scopes={[
                  Scope.SearchExclusiveAgents,
                  Scope.SearchAgentUsers,
                  Scope.SearchPortalUsers,
                  Scope.AlertsSearch,
                ]}
              >
                <NestedList
                  mainListItemTitle="Administration"
                  isExpanded={isAtCurrentRoute(AgencyAdministrationRouteConstants.AGENTS)}
                >
                  <>
                    <RequireScope scope={Scope.AgentNewsAdministration} scopes={props.scopes}>
                      <ListItem
                        selected={isAtCurrentRoute(AgentNewsAdministrationRouteConstants.NewsAdministration)}
                        button
                        onClick={withSidebarClose(() =>
                          props.history.push(AgentNewsAdministrationRouteConstants.NewsAdministration)
                        )}
                      >
                        <ListItemIcon>
                          <i className="fa fa-news sidebar__menu-icon" />
                        </ListItemIcon>
                        <ListItemText>Agent News</ListItemText>
                      </ListItem>
                    </RequireScope>
                    <RequireScope scope={Scope.SearchAgentUsers} scopes={props.scopes}>
                      <ListItem
                        selected={isAtCurrentRoute(CustomerSupportRouteConstants.CUSTOMER_SUPPORT)}
                        button
                        onClick={withSidebarClose(() =>
                          props.history.push(CustomerSupportRouteConstants.CUSTOMER_SUPPORT)
                        )}
                      >
                        <ListItemIcon>
                          <i className="fa fa-user sidebar__menu-icon" />
                        </ListItemIcon>
                        <ListItemText>Customer Support</ListItemText>
                      </ListItem>
                    </RequireScope>
                    <RequireScope scope={Scope.SearchPortalUsers} scopes={props.scopes}>
                      <ListItem
                        selected={isAtCurrentRoute(UserRouteConstants.USERS)}
                        button
                        onClick={withSidebarClose(() => props.history.push(UserRouteConstants.USERS))}
                      >
                        <ListItemIcon>
                          <i className="fa fa-users sidebar__menu-icon" />
                        </ListItemIcon>
                        <ListItemText>Users</ListItemText>
                      </ListItem>
                    </RequireScope>
                    <RequireScope scope={Scope.AlertsSearch} scopes={props.scopes}>
                      <ListItem
                        selected={isAtCurrentRoute(CoreRouteConstants.ALERTS)}
                        button
                        onClick={withSidebarClose(() => props.history.push(CoreRouteConstants.ALERTS))}
                      >
                        <ListItemIcon>
                          <i className="fa fa-bell sidebar__menu-icon" />
                        </ListItemIcon>
                        <ListItemText>Alerts</ListItemText>
                      </ListItem>
                    </RequireScope>
                  </>
                </NestedList>
              </RequireAtLeastOneScope>
              <RequireScope scope={Scope.AgentResources} scopes={props.scopes}>
                <NestedList isExpanded={isAtMyAccessRoute} mainListItemTitle="Documentation">
                  <>
                    <RequireScope scope={Scope.AgentResources} scopes={props.scopes}>
                      <ListItem
                        button
                        onClick={withSidebarClose(() => props.history.push(AgentDocumentationRoutes.ENROLL_MY_WAY))}
                      >
                        <ListItemIcon>
                          <i className="fa fa-briefcase sidebar__menu-icon" />
                        </ListItemIcon>
                        <ListItemText primary={'EnrollMyWay Voice Upload'} />
                      </ListItem>
                    </RequireScope>
                    <RequireScope scope={Scope.AgentResources} scopes={props.scopes}>
                      <ListItem
                        selected={isAtCurrentRoute(AgentDocumentationRoutes.DIRECT_DEPOSIT_INFORMATION)}
                        button
                        id="view-direct-deposit-option"
                        onClick={withSidebarClose(() =>
                          props.history.push(AgentDocumentationRoutes.DIRECT_DEPOSIT_INFORMATION)
                        )}
                      >
                        <ListItemIcon>
                          <i className="fa fa-dollar-sign sidebar__menu-icon" />
                        </ListItemIcon>
                        <ListItemText primary={'Direct Deposit'} />
                      </ListItem>
                    </RequireScope>
                    <RequireScope scope={Scope.AgentResources} scopes={props.scopes}>
                      <ListItem
                        selected={isAtCurrentRoute(AgentDocumentationRouteConstants.PRODUCER_COMPLIANCE_POLICY)}
                        button
                        onClick={withSidebarClose(() =>
                          props.history.push(AgentDocumentationRouteConstants.PRODUCER_COMPLIANCE_POLICY)
                        )}
                      >
                        <ListItemIcon>
                          <i className="fa fa-balance-scale sidebar__menu-icon" />
                        </ListItemIcon>
                        <ListItemText primary={'Producer Compliance Policy'} />
                      </ListItem>
                    </RequireScope>
                  </>
                </NestedList>
              </RequireScope>

              <RequireScope scope={Scope.AgentResources} scopes={props.scopes}>
                <FormMenu onSidebarClose={props.onSidebarClose} />
              </RequireScope>

              {!BR_Footer && (
                <ListItem
                  selected={isAtCurrentRoute(CoreRouteConstants.LEGAL_DOCUMENTS)}
                  button
                  id="view-direct-deposit-option"
                  onClick={withSidebarClose(() => props.history.push(CoreRouteConstants.LEGAL_DOCUMENTS))}
                >
                  <ListItemIcon>
                    <i className="fa fa-balance-scale sidebar__menu-icon" />
                  </ListItemIcon>
                  <ListItemText primary={'Legal Agreements'} />
                </ListItem>
              )}
            </List>
          </div>
        </Drawer>
      </nav>
    </>
  );
};

const mapStateToProps = (state: ReducerState) => ({
  scopes: selectUserScopes(state),
});

export default connect(mapStateToProps)(withRouter(Sidebar));
